import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Real persisted Styles rows for the DL-66146 epic ("D2 Playwright automation — Styles bulk edit
 * Set 2 fixes: confirm-modal copy, hierarchy child validation, multi-value preview", Testmo
 * folder dl-58938). Split out (not appended to `stylesClearFieldFixtures.ts`) because this epic
 * covers a different Testmo folder/scope than DL-62706's clear-fields slice, even though it reuses
 * the same "Print Type" (705) / "Print Placement" (706) hierarchy pair for the child-only-clear
 * case below.
 *
 * Three independent groups of rows:
 * 1. `clearFieldChildOnlyRows` (Testmo 888412) — clearing ONLY the child (mother untouched) must
 *    NOT trigger the hierarchy-mismatch validation error that blocks Next; that error only fires
 *    when the MOTHER changes and leaves a stale child value (see
 *    `clear-fields/hierarchy-dependency-and-apply-audit.spec.ts`'s Testmo #2339514 case).
 * 2. `previewMultiValueRows` (Testmo 888413-888416) — CF 704 "Fabric Blend" (multi-select
 *    AllowedValue, 12 options, `stylesCustomFieldDefinitions.ts`) probing the BulkEditModal
 *    Preview column's `getDisplayValue` join format (`', '`) and `MIXED_VALUES` pill.
 * 3. `columnOrderProbeRow` (Testmo 888418/888419) — a single row carrying real values for the new
 *    709/710/711 CF definitions (grandchild "Print Finish" + "Alpha Field"/"Zeta Field") so the
 *    Styles table actually renders those columns with content, not just empty headers. Column
 *    ORDER itself is driven by the CF *definitions* (`/api/meta/styles/custom-fields`,
 *    `useCustomFieldColumns.ts`'s `insertWithDescendants`), not by row data, but a wholly empty
 *    column is a weaker, less-representative check than one with a real value.
 */

const PRINT_TYPE_CF_ID = 705;
const PRINT_PLACEMENT_CF_ID = 706;
const PRINT_FINISH_CF_ID = 709;
const ZETA_CF_ID = 710;
const ALPHA_CF_ID = 711;
const FABRIC_BLEND_CF_ID = 704;

const SCREEN_PRINT = { id: 7051, value: 'Screen Print' };
const CHEST = { id: 7061, value: 'Chest' };
const MATTE = { id: 7091, value: 'Matte' };

const COTTON = { id: 7041, value: 'Cotton' };
const POLYESTER = { id: 7042, value: 'Polyester' };
const WOOL = { id: 7043, value: 'Wool' };
const ELASTANE = { id: 7044, value: 'Elastane' };
const SILK = { id: 7045, value: 'Silk' };

type Overrides = Partial<StyleRow> & { id: number; name: string; customId: string };

const baseSet2StyleRow = (overrides: Overrides): StyleRow => ({
  description: '',
  state: 'unpublished',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-02-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * DL-66158 (Testmo 888412) — mother (Print Type) untouched, only the child (Print Placement) is
 * directly cleared. Both rows start with the SAME mother/child pair so the pair selection doesn't
 * itself introduce a mismatch; the test's own action clears the child on top of this baseline.
 */
export const clearFieldChildOnlyRows: StyleRow[] = [
  baseSet2StyleRow({
    id: 95101,
    name: 'ClearFieldChildOnly A',
    customId: 'ST-95101',
    customFields: [
      { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
      { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
    ],
  }),
  baseSet2StyleRow({
    id: 95102,
    name: 'ClearFieldChildOnly B',
    customId: 'ST-95102',
    customFields: [
      { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
      { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
    ],
  }),
];

const fabricBlendRow = (
  id: number,
  name: string,
  values: Array<{ id: number; value: string }>
): StyleRow =>
  baseSet2StyleRow({
    id,
    name,
    customId: `ST-${id}`,
    customFields: [{ id: FABRIC_BLEND_CF_ID, type: 'allowedvalue', values }],
  });

/** Testmo 888413 — two rows sharing the SAME 2-value set: Preview shows both, joined by ', '. */
export const previewTwoValueRows: StyleRow[] = [
  fabricBlendRow(95111, 'PreviewMultiValue TwoValues A', [COTTON, POLYESTER]),
  fabricBlendRow(95112, 'PreviewMultiValue TwoValues B', [COTTON, POLYESTER]),
];

/** Testmo 888414 — two rows sharing the SAME 3-value set: all 3 shown, none truncated. */
export const previewThreeValueRows: StyleRow[] = [
  fabricBlendRow(95113, 'PreviewMultiValue ThreeValues A', [COTTON, POLYESTER, WOOL]),
  fabricBlendRow(95114, 'PreviewMultiValue ThreeValues B', [COTTON, POLYESTER, WOOL]),
];

/** Testmo 888415 — two rows sharing the SAME single value (regression: single-value still works). */
export const previewSingleValueRows: StyleRow[] = [
  fabricBlendRow(95115, 'PreviewMultiValue SingleValue A', [COTTON]),
  fabricBlendRow(95116, 'PreviewMultiValue SingleValue B', [COTTON]),
];

/** Testmo 888416 — two rows with DIFFERING value sets: Preview shows "Mixed values". */
export const previewMixedValueRows: StyleRow[] = [
  fabricBlendRow(95117, 'PreviewMultiValue Mixed A', [COTTON, POLYESTER]),
  fabricBlendRow(95118, 'PreviewMultiValue Mixed B', [ELASTANE, SILK]),
];

export const previewMultiValueRows: StyleRow[] = [
  ...previewTwoValueRows,
  ...previewThreeValueRows,
  ...previewSingleValueRows,
  ...previewMixedValueRows,
];

/** Testmo 888418/888419 — a single row carrying real values for the 709/710/711 CF definitions. */
export const columnOrderProbeRow: StyleRow = baseSet2StyleRow({
  id: 95121,
  name: 'ColumnOrderProbe Style',
  customId: 'ST-95121',
  customFields: [
    { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
    { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
    { id: PRINT_FINISH_CF_ID, type: 'nestedallowedvalue', value: MATTE },
    { id: ZETA_CF_ID, type: 'text', value: { id: 0, value: 'Zeta probe value' } },
    { id: ALPHA_CF_ID, type: 'text', value: { id: 0, value: 'Alpha probe value' } },
  ],
});

/**
 * Testmo 888420 (DL-66172) — the end-to-end pass over all three Set 2 fixes in one flow: both
 * rows share the SAME 2-value Fabric Blend set (so Bug 3's Preview join is observable without any
 * edit) and the SAME mother/child hierarchy pair (so Bug 2's mismatch-then-resolve cascade can be
 * driven), leaving Bug 1 (the confirm modal's own copy/count) to fall out of the shared flow all
 * bulk-edit stories go through regardless of which fields changed.
 */
export const endToEndSet2Rows: StyleRow[] = [
  baseSet2StyleRow({
    id: 95131,
    name: 'Set2EndToEnd A',
    customId: 'ST-95131',
    customFields: [
      { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
      { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
      { id: FABRIC_BLEND_CF_ID, type: 'allowedvalue', values: [COTTON, POLYESTER] },
    ],
  }),
  baseSet2StyleRow({
    id: 95132,
    name: 'Set2EndToEnd B',
    customId: 'ST-95132',
    customFields: [
      { id: PRINT_TYPE_CF_ID, type: 'nestedallowedvalue', value: SCREEN_PRINT },
      { id: PRINT_PLACEMENT_CF_ID, type: 'nestedallowedvalue', value: CHEST },
      { id: FABRIC_BLEND_CF_ID, type: 'allowedvalue', values: [COTTON, POLYESTER] },
    ],
  }),
];

/** All new rows for this epic — spread into `buildDefaultStylesData` in `styles.ts`. */
export const bulkEditSet2StyleRows: StyleRow[] = [
  ...clearFieldChildOnlyRows,
  ...previewMultiValueRows,
  columnOrderProbeRow,
  ...endToEndSet2Rows,
];
